import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Callout, Checkbox, Modal, Paragraph, Radio, SegmentedControl } from '@treams/treams-ui';

export default function CreateDepartmentRole(props: {
	source: graphql.User;
	target: graphql.Group;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	refetch: () => Promise<any>;
	onCancel: () => void;
	onCreate: () => void;
}) {
	const { t } = useTranslation();

	const [type, setType] = useState<'manager' | 'coach'>('manager');
	const [role, setRole] = useState<graphql.RoleType>(graphql.RoleType.PrimaryManager);
	const [transitive, setTransitive] = useState(false);

	const [create] = graphql.useAssignRoleMutation();

	function toggleCoachRole() {
		if (role === graphql.RoleType.Coach) {
			setRole(graphql.RoleType.Buddy);
		} else {
			setRole(graphql.RoleType.Coach);
		}
	}

	const roleIDs: Partial<Record<graphql.RoleType, string>> = {
		[graphql.RoleType.Buddy]: 'management-buddy',
		[graphql.RoleType.Coach]: 'management-coach',
		[graphql.RoleType.CollaborativeManager]: 'management-collaborative-manager',
		[graphql.RoleType.PrimaryManager]: 'management-primary-manager',
	};

	return (
		<Modal width={400}>
			<Modal.Contents>
				<Paragraph mb={3}>{t('settings.employees.roles.create.user.instruction')}:</Paragraph>
				<Flex
					mb={3}
					onClick={() => {
						if (type !== 'manager') {
							setType('manager');
							setRole(graphql.RoleType.PrimaryManager);
						}
					}}
				>
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Radio
							checked={type === 'manager'}
							onChange={() => {
								setType('manager');
								setRole(graphql.RoleType.PrimaryManager);
							}}
						/>
					</Box>
					<Box>
						<Paragraph>
							{t('settings.employees.roles.create.explanations.manager.general', {
								source: `${props.source.firstName} ${props.source.lastName}`,
								target: props.target.name,
							})}
						</Paragraph>
						{type === 'manager' && (
							<Box mt={2}>
								<SegmentedControl
									options={[
										{
											title: t('roles.management-primary-manager'),
											value: graphql.RoleType.PrimaryManager,
										},
										{
											title: t('roles.management-collaborative-manager'),
											value: graphql.RoleType.CollaborativeManager,
										},
									]}
									value={role!}
									onChange={(role) => setRole(role as graphql.RoleType)}
								/>
								{role === graphql.RoleType.CollaborativeManager && (
									<Box mt={2}>
										<Callout color="employee">
											{t('settings.employees.roles.create.explanations.manager.collaborative', {
												source: props.source.firstName,
												target: props.target.name,
											})}
										</Callout>
									</Box>
								)}
							</Box>
						)}
					</Box>
				</Flex>
				<Flex
					onClick={() => {
						if (type !== 'coach') {
							setType('coach');
							setRole(graphql.RoleType.Coach);
						}
					}}
				>
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Radio
							checked={type === 'coach'}
							onChange={() => {
								setType('coach');
								setRole(graphql.RoleType.Coach);
							}}
						/>
					</Box>
					<Box>
						<Paragraph>
							{t('settings.employees.roles.create.explanations.coach.general', {
								source: `${props.source.firstName} ${props.source.lastName}`,
								target: props.target.name,
							})}
						</Paragraph>
						{type === 'coach' && (
							<Flex mt={2} onClick={toggleCoachRole}>
								<Box flexGrow={0} flexShrink={0} mr={2}>
									<Checkbox checked={role === graphql.RoleType.Coach} />
								</Box>
								<Box>
									<Paragraph>
										{t('settings.employees.roles.create.explanations.coach.buddy', {
											source: props.source.firstName,
											target: props.target.name,
										})}
									</Paragraph>
								</Box>
							</Flex>
						)}
					</Box>
				</Flex>
				<Flex mt={2} onClick={() => setTransitive((transitive) => !transitive)}>
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Checkbox checked={transitive} />
					</Box>
					<Box>
						<Paragraph>
							{t('settings.employees.roles.create.explanations.transitive', {
								source: props.source.firstName,
								target: props.target.name,
							})}
						</Paragraph>
					</Box>
				</Flex>
			</Modal.Contents>
			<Modal.FormFooter
				small
				onCancel={props.onCancel}
				onSave={async () => {
					await create({
						variables: {
							input: {
								targetID: props.target.id,
								targetType: graphql.RoleTargetType.Department,
								isIndirect: transitive,
								roleID: roleIDs[role!]!,
								userID: props.source.id,
							},
						},
					});
					await props.refetch();
					props.onCreate();
				}}
				disabled={role === undefined}
			/>
		</Modal>
	);
}
